<template>
  <div
    @keydown.down="preventScrollIfCurrent"
    @keydown.up="preventScrollIfCurrent"
    @keyup.down="($event) => next($event, { focus: true, highlight: true })"
    @keyup.up="($event) => previous($event, { focus: true, highlight: true })"
  >
    <slot v-bind="{ currentKey, focusKey, highlightKey, optionsToRender }" />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import {
  firstStickyGroupDataName,
  firstStickyItemDataName,
  lastStickyGroupDataName,
  lastStickyItemDataName,
  obscuredByStickyElement,
} from '@utils/obscuredByStickyElement';

const getCurrentElementsNewIndex = (currentElement: Element) => {
  let element = currentElement;
  let index = 0;

  // find out what the current element's new index is
  while ((element = element.previousElementSibling)) {
    if ((element as HTMLElement).dataset.listItemKey) {
      index++;
    }
  }

  return index;
};

/**
 * Get the distance the container needs to be scrolled so an element that is covered by a sticky element
 * becomes visible
 */
const getScrollDistance = ({
  element,
  baseDistance,
  navigation,
}: {
  element: HTMLElement;
  baseDistance: number;
  navigation: 'previous' | 'next';
}) => {
  let scrollDistance = baseDistance;

  const nextSibling = element.nextElementSibling as HTMLElement;
  const previousSibling = element.previousElementSibling as HTMLElement;

  const nextSiblingIsSticky =
    nextSibling?.dataset[lastStickyGroupDataName] === 'true' || nextSibling?.dataset[lastStickyItemDataName] === 'true';

  const prevSiblingIsSticky = previousSibling?.dataset[firstStickyItemDataName] === 'true';

  if (
    (previousSibling?.previousElementSibling as HTMLElement)?.dataset[firstStickyGroupDataName] === 'true' ||
    nextSibling?.dataset[lastStickyGroupDataName] === 'true'
  ) {
    // a group element means we need to double the scroll distance
    scrollDistance *= 2;
  }

  if ((navigation === 'previous' && !nextSiblingIsSticky) || (navigation === 'next' && prevSiblingIsSticky)) {
    // if the we have moved from a sticky element, the scroll offset is reversed
    scrollDistance *= -1;
  }

  return scrollDistance;
};

/**
 * The ListNavigation component can be used
 * to track position and navigate in another component that renders a list of items.
 *
 * The component keeps information about the current item, and if it is highlighted / focussed
 *
 * focussed is set whenever an element inside a list element is focussed
 *
 * highlighted is set when the current element is highlighted while the list itself is not active,
 * e.g. when the list is controlled via a ListController.
 *
 * The component listens to arrow up / down to navigate the list, focussing previous / next item
 *
 * ListNavigation has been built with SelectionList in mind, but can be used with other components
 * as long as the following requirements are met:
 *
 * - options must be in the same format as for SelectionList
 * - the elements of the list must have a data-list-item-key attribute
 *
 */
export default defineComponent({
  name: 'ListNavigation',
  emits: { 'update:current-key': null, 'focus-current': null },

  props: {
    options: {
      type: Array as PropType<Array<{ [key: string]: string }>>,
      required: true,
    },

    keyProp: {
      type: String,
      required: false,
      default: 'value',
    },

    elementSelector: {
      type: String,
      required: true,
    },

    firstElementSticky: {
      type: Boolean,
      default: false,
    },

    lastElementSticky: {
      type: Boolean,
      default: false,
    },

    gridGap: {
      type: Number,
      default: 16,
    },

    /**
     * If the component should track which elemnts are currently visible, and
     * which ones are almost visible.
     * This info can be used by custom SelectionList components to conditionally render
     * their elements
     */
    trackOptionsToRender: {
      type: Boolean,
      default: false,
    },
  },

  data(): {
    currentIndex: number;
    /**
     * The current item.
     * The current item is tracked by index and by key, to find out if the current
     * element has been moved when the elements in the list change.
     */
    currentKey: string;
    /**
     * The item key that is currenlty focussed
     */
    focusKey: string;
    /**
     * The item key that is currenlty highlighted. Like hover, but for keyboard navigation
     */
    highlightKey: string;
    intersectionObserver: IntersectionObserver;
    intersectionRecords: Map<string, IntersectionObserverEntry>;
    resetHighlightTimeout: ReturnType<typeof setTimeout>;
    resetFocusTimeout: ReturnType<typeof setTimeout>;
    visibleOptions: Map<string, IntersectionObserverEntry>;
    optionsToRender: Set<string>;
  } {
    return {
      currentKey: null,
      focusKey: null,
      highlightKey: null,
      currentIndex: -1,
      intersectionObserver: null,
      intersectionRecords: new Map(),
      resetHighlightTimeout: null,
      resetFocusTimeout: null,
      visibleOptions: new Map(),
      optionsToRender: new Set(),
    };
  },

  methods: {
    /**
     * @public
     */
    startHighlighting(resetIndex = false) {
      if (!this.options.length) {
        return;
      }

      clearTimeout(this.resetHighlightTimeout);

      if (this.currentIndex === -1 || resetIndex) {
        this.currentIndex = 0;
      }

      this.currentKey = this.options[this.currentIndex][this.keyProp];
      this.highlightKey = this.currentKey;

      this.$emit('update:current-key', this.currentKey);
    },

    /**
     * @public
     */
    stopHighlighting() {
      this.highlightKey = null;
    },

    /**
     * @public
     */
    next(
      _$event: KeyboardEvent,
      { highlight = true, focus = true }: { highlight: boolean; focus: boolean } = { highlight: true, focus: true }
    ) {
      let newIndex = 0;

      if (!this.options.length) {
        this.resetPositionData();

        return;
      }

      const wrapAround = this.currentIndex === this.options.length - 1;

      if (this.currentIndex === -1 || wrapAround) {
        newIndex = 0;
      } else {
        newIndex = this.currentIndex + 1;
      }

      this.currentIndex = newIndex;
      this.currentKey = this.options[newIndex][this.keyProp];

      if (highlight) {
        clearTimeout(this.resetHighlightTimeout);
        this.highlightKey = this.currentKey;
      }

      if (focus) {
        clearTimeout(this.resetHighlightTimeout);
        this.focusKey = this.currentKey;
        this.$emit('focus-current', this.currentKey);
      }

      this.$emit('update:current-key', this.currentKey);

      this.scrollToItemIfNeeded(this.currentKey, { isFocussed: focus, navigation: 'next', wrapAround });
    },

    /**
     * @public
     */
    previous(
      _$event: KeyboardEvent,
      { highlight = true, focus = true }: { highlight: boolean; focus: boolean } = { highlight: true, focus: true }
    ) {
      let newIndex = 0;

      if (!this.options.length) {
        this.resetPositionData();

        return;
      }

      const wrapAround = this.currentIndex === 0;

      if (this.currentIndex === -1 || wrapAround) {
        newIndex = this.options.length - 1;
      } else {
        newIndex = this.currentIndex - 1;
      }

      this.currentIndex = newIndex;
      this.currentKey = this.options[newIndex][this.keyProp];

      if (highlight) {
        clearTimeout(this.resetHighlightTimeout);

        this.highlightKey = this.currentKey;
      }

      if (focus) {
        clearTimeout(this.resetHighlightTimeout);

        this.focusKey = this.currentKey;

        this.$emit('focus-current', this.currentKey);
      }

      this.$emit('update:current-key', this.currentKey);
      this.scrollToItemIfNeeded(this.currentKey, { isFocussed: focus, navigation: 'previous', wrapAround });
    },

    /**
     * External update, e.g. when selecting an item in the SelectionList
     * @public
     */
    setPosition(index: number, { highlight = false, focus = false }: { highlight?: boolean; focus?: boolean }) {
      const currentKey = this.options[index]?.[this.keyProp];

      if (currentKey !== undefined) {
        this.currentIndex = index;
        this.currentKey = currentKey;
      }

      if (highlight) {
        clearTimeout(this.resetHighlightTimeout);
        this.resetHighlightTimeout = null;
        this.highlightKey = this.currentKey;
      }

      if (focus) {
        clearTimeout(this.resetFocusTimeout);
        this.resetFocusTimeout = null;
        this.focusKey = this.currentKey;

        this.$emit('focus-current', this.currentKey);
      }
    },

    /**
     * Tries to refocus the current position
     * Used when the element at the current position has been removed
     *
     * @public
     */
    refocus() {
      if (!this.options.length || !this.options[this.currentIndex]) {
        this.resetPositionData();

        return;
      }

      this.currentKey = this.options[this.currentIndex][this.keyProp];

      this.focusKey = this.currentKey;

      this.$emit('focus-current', this.currentKey);
    },

    resetPositionData() {
      this.currentIndex = -1;
      this.currentKey = null;
      this.focusKey = null;
      this.highlightKey = null;

      this.$emit('update:current-key', null);
    },

    /**
     * @public
     */
    resetFocusState() {
      clearTimeout(this.resetHighlightTimeout);
      clearTimeout(this.resetFocusTimeout);
      this.resetHighlightTimeout = null;
      this.resetFocusTimeout = null;

      // Delay reset because other actions might need to
      // know about the highlight / focus state

      // De-highlighting can be stopped by startHighlighting
      this.resetHighlightTimeout = setTimeout(() => {
        // Wait to make sure the element is still in the dom
        // If it has been removed, the MutationObserver handles focus / highlight
        // if (event.target.isConnected) {
        this.highlightKey = null;
        // }
      }, 50);

      // De-focusing can be stopped by the MutationObserver when the focusout
      // was triggered by element removal
      this.resetFocusTimeout = setTimeout(() => {
        // Wait to make sure the element is still in the dom
        // if (event.target.isConnected) {
        this.focusKey = null;
        // }
      }, 50);
    },

    // eslint-disable-next-line max-statements
    scrollToItemIfNeeded(
      key: string,
      {
        isFocussed = true,
        navigation = 'next',
        wrapAround,
      }: { isFocussed?: boolean; navigation?: 'next' | 'previous'; wrapAround?: boolean }
    ) {
      const { element, inView } = this.getElementViewStatus(key);

      if (!isFocussed) {
        // Focussing an element scrolls it into view automatically
        if (element && !inView) {
          element.scrollIntoView({ block: 'nearest' });
        }
      }

      // if first or last el is sticky, we may need to scroll the current el into view manually
      if (element && (this.firstElementSticky || this.lastElementSticky)) {
        // timeout because scrollIntoView is not instant and can't be awaited
        setTimeout(() => {
          if (wrapAround) {
            // if we wrap around, we  know that a sticky element is active and we
            // can simplify the scroll behavior
            const scrollContainer = this.$el.firstElementChild as Element;

            scrollContainer.scrollTop =
              navigation === 'next' ? 0 : scrollContainer.scrollHeight - scrollContainer.clientTop;
          } else {
            // we need to get the current rectangle, the intersection observer might not be up to
            // date because the status didn't change
            const { result, extent } = obscuredByStickyElement(element, this.$el);

            if (result) {
              const scrollContainer = this.$el.firstElementChild as Element;

              scrollContainer.scrollTop += getScrollDistance({
                element,
                baseDistance: extent + this.gridGap,
                navigation,
              });
            }
          }
        }, 50);
      }
    },

    preventScrollIfCurrent(event: KeyboardEvent) {
      if (this.currentIndex > -1) {
        event.preventDefault();
      }
    },

    getElementViewStatus(key: string) {
      const record = this.intersectionRecords.get(key);

      if (!record) return { el: null, inView: false, boundingClientRect: null };

      return {
        element: record.target as HTMLElement,
        inView: record.intersectionRatio === 1,
        boundingClientRect: record.boundingClientRect,
      };
    },

    /**
     * @public
     */
    getCurrent() {
      if (!this.currentKey) return null;

      return this.options.find((option) => option[this.keyProp] === this.currentKey);
    },

    /**
     * Get a list of almost visible options above and below the currently visible scrollarea,
     * enough to "fill" the scrollarea one time above and below
     */
    getAlmostVisibleOptions(): Set<string> {
      let index = 0;

      const almostVisibleOptions = new Set<string>();

      this.visibleOptions.forEach((entry) => {
        // if first / last elemen are sticky the second / second to last element becomes the reference element
        if (
          index === 0 ||
          (index === 1 && this.firstElementSticky) ||
          index === this.visibleOptions.size - 1 ||
          (index === this.visibleOptions.size - 2 && this.lastElementSticky)
        ) {
          const siblingSelector = index === 0 || index === 1 ? 'previousElementSibling' : 'nextElementSibling';

          let sibling = entry.target[siblingSelector];

          const maxCount = 20;
          let accHeight = 0;
          let count = 0;

          while (sibling && accHeight <= entry.rootBounds.height && count <= maxCount) {
            const key = (sibling as HTMLElement).dataset.listItemKey;

            // sibling is a list item. If it's not, try with the next sibling
            if (key) {
              // access the record to prevent dom access for the height
              const record = this.intersectionRecords.get(key);

              if (record) {
                accHeight += record.boundingClientRect.height;
                almostVisibleOptions.add(key);
                count++;
              } else {
                sibling = null;
              }
            }

            sibling = sibling[siblingSelector];
          }
        }

        index++;
      });

      return almostVisibleOptions;
    },
  },

  mounted() {
    const scrollContainer = this.$el as Element;

    /**
     * Intersection observer is used to determine if an element is visible
     * when the focussed or highlighted element is changed.
     * If it's not visible, it's scrolled into view
     */
    const intersectionOptions: IntersectionObserverInit = {
      root: scrollContainer,
      rootMargin: '0px',
      threshold: 0.75,
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const key = (entry.target as HTMLElement).dataset.listItemKey;

        this.intersectionRecords.set(key, entry);

        if (this.trackOptionsToRender) {
          if (entry.isIntersecting) {
            this.visibleOptions.set(key, entry);
          } else {
            this.visibleOptions.delete(key);
          }
        }
      });

      this.optionsToRender = new Set<string>([...this.visibleOptions.keys(), ...this.getAlmostVisibleOptions()]);
    }, intersectionOptions);

    scrollContainer.querySelectorAll(this.elementSelector).forEach((element) => {
      this.intersectionObserver.observe(element);
    });

    /**
     * The mutation observer
     * - adds new elements to the intersection observer
     * - corrects the index and key when elements are added / removed
     */
    const mutationObserver = new MutationObserver((mutations) => {
      let selectElementsAddedOrRemoved = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && (node as HTMLElement).dataset.listItemKey) {
            selectElementsAddedOrRemoved = true;
            this.intersectionObserver.observe(node as Element);
          }
        });

        mutation.removedNodes.forEach((node) => {
          const key = node.nodeType === 1 && (node as HTMLElement).dataset.listItemKey;

          if (key) {
            selectElementsAddedOrRemoved = true;
            const element = node as HTMLElement;
            this.intersectionObserver.unobserve(element);
            this.visibleOptions.delete(key);
          }
        });
      });

      if (selectElementsAddedOrRemoved) {
        const currentElement =
          this.currentIndex === -1 ? null : this.$el.querySelector(`[data-list-item-key="${this.currentKey}"]`);

        // current element may have moved
        if (currentElement) {
          this.currentIndex = getCurrentElementsNewIndex(currentElement);

          if (this.highlightKey === this.currentKey) {
            this.$nextTick(() => {
              this.scrollToItemIfNeeded(this.currentKey, { isFocussed: false });
            });
          }
        }
      }
    });

    const mutationConfig = { childList: true };

    mutationObserver.observe(scrollContainer.firstElementChild, mutationConfig);
  },

  beforeUnmount() {
    this.intersectionObserver?.disconnect();
    clearTimeout(this.resetHighlightTimeout);
    clearTimeout(this.resetFocusTimeout);
    this.resetHighlightTimeout = null;
    this.resetFocusTimeout = null;
  },

  watch: {
    /**
     * Watch the options, and if the current option does not exist anymore:
     * - set the position data to the first option
     * - highlight the first option if highlighting was on, and focus it if the current option was focussed
     *
     * This is not done in the mutation observer above because it needs to be ready
     * before the DOM updates (for external list controllers)
     */
    options: {
      handler() {
        if (!this.currentKey && !this.focusKey) return;

        const currentItem = this.currentKey && this.options.find((option) => option[this.keyProp] === this.currentKey);

        const currentItemRemoved = !currentItem;

        if (currentItemRemoved) {
          const highlight = Boolean(this.highlightKey);
          const hadFocus = this.focusKey === this.currentKey;

          this.resetPositionData();

          this.next(null, { highlight, focus: hadFocus });
        }
      },
      deep: true,
    },
  },
});
</script>
